import { useState, useEffect, useCallback } from 'react';
import {
    Heading,
    Flex,
    Box,
    SimpleGrid,
    useDisclosure,
    Button,
    Divider,
    Spacer,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Accordion,
    Text,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled/macro';
import { useSelector } from 'react-redux';
import { HashLink } from 'react-router-hash-link';

import { checkItemInputEvent, flatItemDescriptions } from '../../services/items';
import { getForwardCurveDefinitions, editForwardCurve } from '../../services/forwardCurve';
import { preserveConnectionSearchParams } from '../../services/connections';

import useCommonToast from '../../hooks/useCommonToast';
import useHistoryPush from '../../hooks/useHistoryPush';

import InputFormikField from '../forms/InputFormikField';

import Error from '../utils/Error';
import DefinitionsSkeleton from '../utils/DefinitionsSkeleton';
import ComboboxFormikField from '../forms/ComboboxFormikField';
import ConfirmationModal from '../utils/ConfirmationModal';
import CircularIconButton from '../utils/CircularIconButton';
import Tooltip from '../utils/Tooltip';
import AddSubItem from '../utils/AddSubItem';

import AddForwardItemModal from './AddForwardItemModal';
import ForwardCurveDefinition from './ForwardCurveDefinition';

import { ReactComponent as TrendIcon } from '../../icons/trend.svg';
import { ReactComponent as SaveIcon } from '../../icons/save.svg';
import { ReactComponent as EditIcon } from '../../icons/edit-circled.svg';
import { ReactComponent as ChevronDownIcon } from '../../icons/chevron-down.svg';

const ForwardItem = () => {
    const { forwardItemId } = useParams();
    const itemTypes = useSelector((state) => state.item.itemTypes);
    const itemType = 'Forward Curve';
    const itemTypeId = itemTypes.length > 0 && itemTypes.find((item) => item.description === itemType).itemTypeId;

    const intl = useIntl();
    const toast = useCommonToast();
    const historyPush = useHistoryPush();

    const deleteItemModal = useDisclosure();
    const addSubItemModal = useDisclosure();

    const itemId = Number(forwardItemId);

    const books = useSelector((state) => state.item.dataForItemCreation.books);
    const locations = useSelector((state) => state.item.dataForItemCreation.locations);

    const [selectedItem, setSelectedItem] = useState(null);
    const [forwardItemData, setForwardItemData] = useState(null);
    const [loadingError, setLoadingError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [editDescriptionConfig, setEditDescriptionConfig] = useState(null);

    const formLabels = {
        commodityType: intl.formatMessage({ id: 'basis_commodity_type' }),
        selectPlaceholder: intl.formatMessage({ id: 'common_forms_select_option' }),
    };

    const initialFormValues = {
        description: selectedItem?.description,
        bookId: selectedItem?.bookId,
        locationId: selectedItem?.locationId,
        definitions: {},
    };

    forwardItemData?.forwardCurveDefinitionResponses.forEach((def) => {
        initialFormValues.definitions[def.id] = {};

        initialFormValues.definitions[def.id]['id'] = def.id;
        initialFormValues.definitions[def.id]['description'] = def.description;
        initialFormValues.definitions[def.id]['commodityId'] = def.commodityId || '';
        initialFormValues.definitions[def.id]['marketScaler'] = def.marketScaler || '';
        initialFormValues.definitions[def.id]['peakPeriodLabelId'] = def.peakPeriodLabelId || '';
        initialFormValues.definitions[def.id]['currencyID'] = def.currencyID || '';
        initialFormValues.definitions[def.id]['marketId'] = def.marketId || '';
        initialFormValues.definitions[def.id]['useCurveDeveloper'] = def.useCurveDeveloper || '';

        return initialFormValues.definitions[def.id];
    });

    const fetchForwardItemDefinitions = useCallback(() => {
        return getForwardCurveDefinitions(itemId)
            .then((data) => {
                setSelectedItem(data.forwardPriceItem);
                setForwardItemData(data);
                setEditDescriptionConfig(flatItemDescriptions(data.forwardCurveDefinitionResponses, itemId));

                // This is needed to show results after previous error
                setLoadingError(false);
            })
            .catch(() => setLoadingError(true))
            .finally(() => setIsLoading(false));
    }, [itemId]);

    useEffect(() => {
        fetchForwardItemDefinitions();
    }, [fetchForwardItemDefinitions]);

    const validateItemDescription = (value, itemId, charLength) => {
        const trimmed = value.trim();
        const label = `${intl.formatMessage({ id: 'forward_curve_label' })} name`;

        let errorMessage = '';

        if (trimmed.length === 0) {
            errorMessage = intl.formatMessage({ id: 'common_forms_validation_required' }, { label });
        } else if (trimmed.length > charLength) {
            errorMessage = intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label, lengthRule: `${charLength} characters or less` }
            );
        }

        if (errorMessage !== '') {
            setEditDescriptionConfig((prevState) => {
                return {
                    ...prevState,
                    [itemId]: { ...prevState[itemId], penDisabled: true },
                };
            });
        } else {
            setEditDescriptionConfig((prevState) => {
                return {
                    ...prevState,
                    [itemId]: { ...prevState[itemId], penDisabled: false },
                };
            });
        }

        return errorMessage;
    };

    const onFormSubmit = async ({ bookId, locationId, description, definitions }) => {
        const successMessage = intl.formatMessage({ id: 'forward_curve_save_changes_success' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        const subForwardCurves = [];

        Object.values(definitions).forEach((subForwardCurve) => {
            subForwardCurves.push({
                ...subForwardCurve,
                itemId: selectedItem.id,
                marketScaler: subForwardCurve.subForwardCurve || null,
                currencyID: subForwardCurve.currencyID || null,
                marketId: subForwardCurve.marketId || null,
                useCurveDeveloper: subForwardCurve.useCurveDeveloper || false,
            });
        });

        try {
            await editForwardCurve({
                forwardCurveItem: {
                    description,
                    bookId,
                    itemTypeId,
                    locationId,
                    itemId: selectedItem.id,
                },
                subForwardCurves,
            });
            toast(successMessage);

            historyPush({
                pathname: '/basis-configurator',
                hash: '#forward-price',
                search: preserveConnectionSearchParams(window.location.search),
            });
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    // TBA
    const onItemDelete = async (targetBasis) => {
        // const successMessage = intl.formatMessage({ id: 'common_delete_success' }, { item: 'basis' });
        // const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });
        // try {
        //     await deleteBasis(targetBasis.basisId);
        //     onBasisDelete(targetBasis);
        //     historyPush('/basis');
        //     toast(successMessage);
        // } catch (error) {
        //     if (error.response.status !== 401) {
        //         toast({
        //             status: 'error',
        //             message: errorMessage,
        //         });
        //     }
        // }
    };

    const onEditDescription = (itemId) => {
        if (!editDescriptionConfig[itemId].penDisabled) {
            setEditDescriptionConfig((prevState) => {
                return {
                    ...prevState,
                    [itemId]: { isActive: !prevState[itemId].isActive, penDisabled: false },
                };
            });
        }
    };

    const onAddSubItem = (newSubItem) => {
        setEditDescriptionConfig((prevState) => ({
            ...prevState,
            [newSubItem.id]: { isActive: false, penDisabled: false },
        }));

        setForwardItemData((prev) => ({
            ...prev,
            forwardCurveDefinitionResponses: [...prev.forwardCurveDefinitionResponses, newSubItem],
        }));

        addSubItemModal.onClose();
    };

    return (
        <Box pt={'80px'} pb={'50px'}>
            {isLoading ? (
                <DefinitionsSkeleton />
            ) : loadingError ? (
                <Box m={6}>
                    <Error primaryId="common_error" secondaryId="common_loading_error" additionalText="basis" />
                </Box>
            ) : (
                <>
                    <Breadcrumb
                        pt="12px"
                        px="24px"
                        fontSize={14}
                        my={4}
                        spacing="8px"
                        separator={<ChevronRightIcon color="gray.100" />}
                    >
                        <BreadcrumbItem>
                            <BreadcrumbLink>
                                <HashLink
                                    to={{
                                        pathname: `/basis-configurator`,
                                        hash: `#forward-price`,
                                        search: preserveConnectionSearchParams(window.location.search),
                                    }}
                                >
                                    <FormattedMessage id="basis_setup" />
                                </HashLink>
                            </BreadcrumbLink>
                        </BreadcrumbItem>

                        <BreadcrumbItem>
                            <BreadcrumbLink color={'blue.500'} textTransform="capitalize">
                                <FormattedMessage id="forward_curve_breadcrumb" />
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                    </Breadcrumb>
                    <Formik
                        validateOnChange={false}
                        enableReinitialize
                        initialValues={initialFormValues}
                        onSubmit={onFormSubmit}
                    >
                        {({ isSubmitting, dirty, isValid, values, errors }) => (
                            <Form>
                                <ActionBar>
                                    <Flex align="center">
                                        <CustomTrendIcon isMaster={selectedItem.securityId} />
                                        {!editDescriptionConfig[selectedItem.id]?.isActive ? (
                                            <Heading px={2} as="h2" variant="h2">
                                                {values.description}
                                            </Heading>
                                        ) : (
                                            <InputFormikField
                                                px={3}
                                                flex="1"
                                                maxW="500px"
                                                validate={(value) =>
                                                    validateItemDescription(value, selectedItem.id, 100)
                                                }
                                                name="description"
                                                type="text"
                                                onKeyDown={checkItemInputEvent}
                                                isFastField
                                            />
                                        )}

                                        <Box display="inline-block" mr={4}>
                                            <Tooltip
                                                label={intl.formatMessage({ id: 'common_edit_btn_tooltip_and_label' })}
                                            >
                                                <CircularIconButton
                                                    aria-label={intl.formatMessage({
                                                        id: 'common_edit_btn_tooltip_and_label',
                                                    })}
                                                    leftIcon={<EditIcon />}
                                                    onClick={() => onEditDescription(selectedItem.id)}
                                                />
                                            </Tooltip>
                                        </Box>
                                    </Flex>

                                    <Spacer display={{ base: 'none', xl: 'block' }} />

                                    <Button variant="secondary" onClick={() => {}}>
                                        <Box as="span" textTransform="capitalize">
                                            <FormattedMessage id="common_cancel" />
                                        </Box>
                                    </Button>

                                    <Button
                                        leftIcon={<SaveIcon />}
                                        isDisabled={!isValid || !dirty}
                                        isLoading={isSubmitting}
                                        type="submit"
                                        size="sm"
                                        variant="special-green"
                                    >
                                        <FormattedMessage id="forward_curve_save_and_continue" />
                                    </Button>
                                </ActionBar>

                                <Divider />

                                <SimpleGrid pt={7} pb={5} px={6} columns={{ xl: 3 }} spacingX={6} spacingY={4}>
                                    <Box>
                                        <Text
                                            fontSize={12}
                                            color="gray.600"
                                            fontWeight="500"
                                            mb={1}
                                            textTransform="capitalize"
                                        >
                                            <FormattedMessage id="forward_curve_location" />
                                        </Text>
                                        <ComboboxFormikField
                                            id="locationId"
                                            name="locationId"
                                            label={formLabels.location}
                                            options={locations}
                                            labelKey="description"
                                            valueKey="id"
                                            showPlaceholder={true}
                                            placeholderValue=""
                                            isFastField
                                        />
                                    </Box>
                                    <Box>
                                        <Text
                                            fontSize={12}
                                            color="gray.600"
                                            fontWeight="500"
                                            mb={1}
                                            textTransform="capitalize"
                                        >
                                            <FormattedMessage id="forward_curve_book" />
                                        </Text>
                                        <ComboboxFormikField
                                            id="bookId"
                                            name="bookId"
                                            label={formLabels.book}
                                            options={books}
                                            labelKey="name"
                                            valueKey="id"
                                            showPlaceholder={true}
                                            placeholderValue=""
                                            isFastField
                                        />
                                    </Box>
                                </SimpleGrid>

                                <Box>
                                    {forwardItemData.forwardCurveDefinitionResponses.length === 0 ? (
                                        <AddSubItem onOpen={addSubItemModal.onOpen} />
                                    ) : (
                                        <CustomAccordion allowToggle defaultIndex={[0]}>
                                            {forwardItemData.forwardCurveDefinitionResponses.map((def) => {
                                                return (
                                                    <ForwardCurveDefinition
                                                        key={def.id}
                                                        forwardItemData={forwardItemData}
                                                        forwardItemDefinitionData={def}
                                                        onAddSubItemModalOpen={addSubItemModal.onOpen}
                                                        editDescriptionConfig={editDescriptionConfig}
                                                        onEditDescription={onEditDescription}
                                                        validateItemDescription={validateItemDescription}
                                                    />
                                                );
                                            })}
                                        </CustomAccordion>
                                    )}
                                </Box>
                            </Form>
                        )}
                    </Formik>

                    {deleteItemModal.isOpen && (
                        <ConfirmationModal
                            isOpen
                            onClose={deleteItemModal.onClose}
                            header={<FormattedMessage id="common_delete_modal_heading" values={{ item: 'Basis' }} />}
                            hasExtraStep
                            content={
                                <FormattedMessage
                                    id="common_delete_modal_msg"
                                    values={{
                                        item: selectedItem.description,
                                    }}
                                />
                            }
                            confirmText={
                                <Box as="span" textTransform="capitalize">
                                    <FormattedMessage id="common_delete" />
                                </Box>
                            }
                            onConfirm={() => {
                                deleteItemModal.onClose();
                                onItemDelete(selectedItem);
                            }}
                        />
                    )}

                    {addSubItemModal.isOpen && (
                        <AddForwardItemModal
                            isOpen
                            onClose={addSubItemModal.onClose}
                            forwardItem={forwardItemData}
                            onAddSubItem={onAddSubItem}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

const CustomTrendIcon = styled(TrendIcon, { shouldForwardProp: (prop) => prop !== 'isMaster' })`
    height: 32px;
    width: 32px;
    flex-shrink: 0;

    path {
        fill: ${(props) => (props.isMaster ? 'var(--chakra-colors-green-500)' : 'var(--chakra-colors-gray-600)')};
    }
`;

const ActionBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 12px 24px;
    gap: 16px;

    button {
        text-transform: capitalize;
        width: 100%;
    }

    button:first-of-type {
        margin-top: 4px;
    }

    @media (min-width: 769px) {
        flex-direction: row;

        button:first-of-type {
            margin-top: 0px;
        }
    }

    @media (min-width: 1280px) {
        button {
            width: auto;
        }
    }
`;

const ChevronRightIcon = styled(ChevronDownIcon)`
    transform: rotate(-90deg);
    circle {
        color: transparent;
    }
    opacity: 0.5;
`;

const CustomAccordion = styled(Accordion)`
    .chakra-accordion__item:last-child {
        border-bottom: 1px solid var(--chakra-colors-gray-200);
    }
`;

export default ForwardItem;
